import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';
import SettingsErrorAlert from './SettingsErrorAlert';
import { groupMethodsByCategory } from '../../constants/mfa';

const MfaSettings = () => {
  const { can } = usePermissions();
  const canEdit = can('settings', 'edit');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState({
    methods: {},
    method_catalog: [],
    method_categories: [],
    require_at_least_one_mfa: false,
    step_up_authentication: false
  });

  const methodCatalog = config.method_catalog || [];
  const groupedMethods = useMemo(
    () => groupMethodsByCategory(methodCatalog, config.method_categories?.map((row) => row.key)),
    [methodCatalog, config.method_categories]
  );

  const enabledMethodCount = useMemo(
    () => Object.values(config.methods || {}).filter((method) => method.enabled).length,
    [config.methods]
  );

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/settings/mfa');
      setConfig(response.data.data);
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to load MFA settings',
        path: err.response?.data?.path,
        status: err.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const getMethodDefaults = (method) => ({
    strict_app_binding: method.default_strict_app_binding ?? false
  });

  const setMethodEnabled = (key, enabled, methodDefaults) => {
    setConfig((prev) => ({
      ...prev,
      methods: {
        ...prev.methods,
        [key]: {
          ...(prev.methods[key] || {}),
          enabled,
          strict_app_binding: prev.methods[key]?.strict_app_binding ?? methodDefaults.strict_app_binding
        }
      }
    }));
  };

  const setStrictAppBinding = (key, strictAppBinding) => {
    setConfig((prev) => ({
      ...prev,
      methods: {
        ...prev.methods,
        [key]: {
          ...(prev.methods[key] || {}),
          strict_app_binding: strictAppBinding,
          enabled: strictAppBinding ? true : !!prev.methods[key]?.enabled
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put('/settings/mfa', {
        methods: config.methods,
        require_at_least_one_mfa: config.require_at_least_one_mfa,
        step_up_authentication: config.step_up_authentication
      });
      toast.success('MFA settings saved');
      load();
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to save MFA settings',
        path: err.response?.data?.path,
        status: err.response?.status
      });
    } finally {
      setSaving(false);
    }
  };

  const renderMethodRow = (method) => {
    const methodConfig = config.methods?.[method.key] || {};
    const defaults = getMethodDefaults(method);

    return (
      <Box key={method.key} sx={{ py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={600}>{method.label}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {method.description
                || (method.type === 'email' ? 'Email one-time passcode' : 'Authenticator app (TOTP)')}
            </Typography>
          </Box>
          <Switch
            checked={!!methodConfig.enabled}
            onChange={(e) => setMethodEnabled(method.key, e.target.checked, defaults)}
            disabled={!canEdit}
          />
        </Box>
        {method.type === 'totp' && methodConfig.enabled && (
          <FormControlLabel
            sx={{ ml: 0, mt: 1 }}
            control={
              <Switch
                size="small"
                checked={!!methodConfig.strict_app_binding}
                onChange={(e) => setStrictAppBinding(method.key, e.target.checked)}
                disabled={!canEdit}
              />
            }
            label={`Enrollment: require ${method.required_app_label || method.label} only (blocks other apps at setup)`}
          />
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Multi-factor Authentication</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>

      {error && <SettingsErrorAlert message={error.message} path={error.path} status={error.status} />}

      <Alert severity="info" sx={{ mb: 3 }}>
        Enable as many MFA methods as you need ({enabledMethodCount} currently enabled). Users choose
        which enabled methods to enroll from Profile. Step-up authentication requires each user to
        enroll at least two methods.
      </Alert>

      {groupedMethods.map((group) => (
        <Paper key={group.key} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{group.label}</Typography>
          {group.methods.map(renderMethodRow)}
        </Paper>
      ))}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Security Policies</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={!!config.require_at_least_one_mfa}
              onChange={(e) => setConfig((prev) => ({
                ...prev,
                require_at_least_one_mfa: e.target.checked,
                step_up_authentication: e.target.checked ? prev.step_up_authentication : false
              }))}
              disabled={!canEdit}
            />
          }
          label='Require at least one MFA method'
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 7, mb: 2 }}>
          Users must enroll at least one enabled MFA method before they can sign in.
        </Typography>
        <Divider sx={{ my: 2 }} />
        <FormControlLabel
          control={
            <Switch
              checked={!!config.step_up_authentication}
              onChange={(e) => setConfig((prev) => ({
                ...prev,
                step_up_authentication: e.target.checked,
                require_at_least_one_mfa: e.target.checked ? true : prev.require_at_least_one_mfa
              }))}
              disabled={!canEdit}
            />
          }
          label="Step-up authentication (two methods at login)"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 7 }}>
          Each user must enroll two or more enabled MFA methods. At login, two methods are verified
          in random order.
        </Typography>
      </Paper>

      {canEdit && (
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save MFA Settings'}
        </Button>
      )}
    </Box>
  );
};

export default MfaSettings;
